import type { Module } from "vuex";

import type { MasterTemplate } from "hoxton-types";

import { MultiviewMode } from "@/enums/multiview";
import type { RootState } from "@/store/types";

const storeNamespace = "masterTemplateLibrary";

export const MasterTemplateLibraryMutation = {
    RemoveMasterTemplates: "removeMasterTemplates",
    SetMasterTemplate: "setMasterTemplate",
    SetUploadConfig: "setUploadConfig",
    SetSelectViewMode: "setSelectViewMode",
    UpdateFamilyFilter: "updateFamilyFilter",
    UpdateResizedFilter: "updateResizedFilter",
    UpdateSizeFilter: "updateSizeFilter",
    UpdateTextFilter: "updateTextFilter",
    UpdateTypeFilter: "updateTypeFilter"
};

export const MasterTemplateLibraryAction = {
    SetMasterTemplates: `${storeNamespace}/setMasterTemplates`,
    SetUploadConfig: `${storeNamespace}/${MasterTemplateLibraryMutation.SetUploadConfig}`,
    SetSelectViewMode: `${storeNamespace}/${MasterTemplateLibraryMutation.SetSelectViewMode}`,
    SyncFilters: `${storeNamespace}/syncFilters`,
    UpdateFamilyFilter: `${storeNamespace}/${MasterTemplateLibraryMutation.UpdateFamilyFilter}`,
    UpdateResizedFilter: `${storeNamespace}/${MasterTemplateLibraryMutation.UpdateResizedFilter}`,
    UpdateSizeFilter: `${storeNamespace}/${MasterTemplateLibraryMutation.UpdateSizeFilter}`,
    UpdateTextFilter: `${storeNamespace}/${MasterTemplateLibraryMutation.UpdateTextFilter}`,
    UpdateTypeFilter: `${storeNamespace}/${MasterTemplateLibraryMutation.UpdateTypeFilter}`
};

export const MasterTemplateLibraryGetters = {
    filteredMasterTemplateIds: `${storeNamespace}/filteredMasterTemplateIds`,
    templateFamiliesByParentId: `${storeNamespace}/templateFamiliesByParentId`
};

// TODO - remove it after filtering refactor and use the one from enums/masterTemplate.ts
export const FilterType = {
    Campaign: "Campaign",
    Family: "Family",
    GroupValue: "GroupValue",
    Language: "Language",
    Resized: "Resized",
    Size: "Size",
    Status: "Status",
    Text: "Text",
    Type: "Type"
};

export type FilterTypeType = (typeof FilterType)[keyof typeof FilterType];

const updateFilter = (state: State, filter: Filter, filterType: FilterTypeType) => {
    const filters = [...state.filters];
    const filterIndex = filters.findIndex(fi => fi.type === filterType);

    if (filter.values.length) {
        if (filterIndex === -1) {
            // add new filter
            filters.push(filter);
        } else {
            // update filter
            filters.splice(filterIndex, 1, filter);
        }
    } else {
        // remove filter
        filters.splice(filterIndex, 1);
    }

    return filters;
};

type Filter = { type: FilterTypeType; values: string[] };

export type State = {
    filters: Filter[];
    masterTemplateIds: string[];
    masterTemplates: Record<string, MasterTemplate>;
    uploadConfig: {
        clientId: string;
        config: { __typename: "TemporaryUploadConfig"; filePrefix: string; action: string; postData: string };
    } | null;
    selectViewMode: (typeof MultiviewMode)[keyof typeof MultiviewMode];
};

type Getters = {
    filteredBySize: (masterTemplateIds: string[]) => string[];
    filteredByType: (masterTemplateIds: string[]) => string[];
    filteredByText: (masterTemplateIds: string[]) => string[];
    filterByFamily: (masterTemplateIds: string[]) => string[];
    filteredByResized: (masterTemplateIds: string[]) => string[];
    filteredMasterTemplateIds: () => string[];
    templateFamiliesByParentId: () => Record<string, { parentId: string | null; children: string[] }>;
};

const getDefaultState = (): State => ({
    filters: [],
    masterTemplates: {},
    masterTemplateIds: [],
    uploadConfig: null,
    selectViewMode: MultiviewMode.Grid
});

const masterTemplateLibraryModule: Module<State, RootState> = {
    namespaced: true,
    state: getDefaultState(),
    getters: {
        filterByFamily: state => (masterTemplateIds: State["masterTemplateIds"]) => {
            const familyFilter = state.filters.filter(filter => filter.type === FilterType.Family);

            if (!familyFilter.length) {
                return masterTemplateIds;
            }

            const parentIds = familyFilter.pop()?.values;

            return masterTemplateIds.filter(
                masterTemplateId =>
                    parentIds?.includes(masterTemplateId) ??
                    parentIds?.includes(state.masterTemplates[masterTemplateId]?.parentId ?? "")
            );
        },

        filteredByResized: state => (masterTemplateIds: State["masterTemplateIds"]) => {
            const resizedFilter = state.filters.filter(filter => filter.type === FilterType.Resized);
            if (resizedFilter.length) {
                const hideResized = resizedFilter.pop()?.values.pop();

                if (hideResized === "hide") {
                    return masterTemplateIds.filter(
                        masterTemplateId => !state.masterTemplates[masterTemplateId]?.parentId
                    );
                }
            }

            return masterTemplateIds;
        },

        filteredBySize: state => (masterTemplateIds: State["masterTemplateIds"]) => {
            const sizeFilters = state.filters.filter(filter => filter.type === FilterType.Size);

            if (!sizeFilters.length) {
                return masterTemplateIds;
            }

            const sizes = sizeFilters.pop()?.values.map(size => {
                const sizeArray = size.split("x");

                return {
                    width: parseInt(sizeArray[0]!, 10),
                    height: parseInt(sizeArray[1]!, 10)
                };
            });

            return masterTemplateIds.filter(masterTemplateId =>
                sizes?.some(
                    sizeFilter =>
                        sizeFilter.width === state.masterTemplates[masterTemplateId]?.width &&
                        sizeFilter.height === state.masterTemplates[masterTemplateId].height
                )
            );
        },

        filteredByText: state => (masterTemplateIds: State["masterTemplateIds"]) => {
            const textFilters = state.filters.filter(filter => filter.type === FilterType.Text);
            if (!textFilters.length) {
                return masterTemplateIds;
            }

            const textFilter = textFilters.pop()?.values as unknown as string;

            return masterTemplateIds.filter(masterTemplateId =>
                state.masterTemplates[masterTemplateId]?.name.toLowerCase().includes(textFilter.toLowerCase())
            );
        },

        filteredByType: state => (masterTemplateIds: State["masterTemplateIds"]) => {
            const typeFilters = state.filters.filter(filter => filter.type === FilterType.Type);

            if (!typeFilters.length) {
                return masterTemplateIds;
            }

            const selectedTemplateTypes = typeFilters.pop()?.values;

            return masterTemplateIds.filter(masterTemplateId =>
                selectedTemplateTypes?.includes(state.masterTemplates[masterTemplateId]?.adType ?? "")
            );
        },

        filteredMasterTemplateIds: (state, getters: Getters) => {
            const filteredBySize = getters.filteredBySize(state.masterTemplateIds);
            const filteredByType = getters.filteredByType(filteredBySize);
            const filteredByText = getters.filteredByText(filteredByType);
            const filteredByFamily = getters.filterByFamily(filteredByText);
            const filteredByResized = getters.filteredByResized(filteredByFamily);

            return filteredByResized;
        },

        templateFamiliesByParentId: state => {
            const familiesByParentId = state.masterTemplateIds.reduce<
                Record<string, { parentId: string | null; children: string[] }>
            >((acc, templateId) => {
                const template = state.masterTemplates[templateId];

                if (!template) {
                    return acc;
                }

                const id = template.parentId ?? template._id;

                acc[id] = acc[id] ?? { parentId: null, children: [] };

                if (!template.parentId) {
                    acc[template._id]!.parentId = template._id;
                } else {
                    acc[template.parentId]!.children.push(template._id);
                }

                return acc;
            }, {});

            return familiesByParentId;
        }
    },

    mutations: {
        removeMasterTemplates(state) {
            state.masterTemplateIds = [];
        },

        setMasterTemplate(state, masterTemplate: MasterTemplate) {
            const masterTemplateId = masterTemplate._id;
            state.masterTemplates[masterTemplateId] = masterTemplate;

            if (!state.masterTemplateIds.includes(masterTemplateId)) {
                state.masterTemplateIds.push(masterTemplateId);
            }
        },

        setUploadConfig(state, uploadConfig: State["uploadConfig"]) {
            state.uploadConfig = uploadConfig;
        },

        setSelectViewMode(state, mode: State["selectViewMode"]) {
            state.selectViewMode = mode;
        },

        updateFamilyFilter(state, filter: Filter) {
            state.filters = updateFilter(state, filter, FilterType.Family);
        },

        updateResizedFilter(state, filter: Filter) {
            state.filters = updateFilter(state, filter, FilterType.Resized);
        },

        updateSizeFilter(state, filter: Filter) {
            state.filters = updateFilter(state, filter, FilterType.Size);
        },

        updateTextFilter(state, filter: Filter) {
            state.filters = updateFilter(state, filter, FilterType.Text);
        },

        updateTypeFilter(state, filter: Filter) {
            state.filters = updateFilter(state, filter, FilterType.Type);
        }
    },

    actions: {
        setMasterTemplates({ commit }, masterTemplates: MasterTemplate[]) {
            commit(MasterTemplateLibraryMutation.RemoveMasterTemplates);

            if (!masterTemplates.length) {
                return;
            }

            masterTemplates.forEach(masterTemplate => {
                commit(MasterTemplateLibraryMutation.SetMasterTemplate, masterTemplate);
            });
        },

        setUploadConfig({ commit }, uploadConfig: State["uploadConfig"]) {
            commit(MasterTemplateLibraryMutation.SetUploadConfig, uploadConfig);
        },

        setSelectViewMode({ commit }, viewMode: State["selectViewMode"]) {
            commit(MasterTemplateLibraryMutation.SetSelectViewMode, viewMode);
        },

        syncFilters({ commit, state }) {
            state.filters.forEach(filter => {
                if (filter.type === FilterType.Size) {
                    const masterTemplateSizes = Object.keys(
                        state.masterTemplateIds.reduce(
                            (acc, cur) =>
                                Object.assign(acc, {
                                    [`${state.masterTemplates[cur]?.width}x${state.masterTemplates[cur]?.height}`]: true
                                }),
                            []
                        )
                    );

                    if (!filter.values.every(filterLanguage => masterTemplateSizes.includes(filterLanguage))) {
                        const updated = {
                            ...filter,
                            values: filter.values.filter(filterLanguage => masterTemplateSizes.includes(filterLanguage))
                        };

                        commit(MasterTemplateLibraryMutation.UpdateSizeFilter, updated);
                    }
                }
            });
        },

        updateResizedFilter({ commit }, filter: { values: string[] }) {
            commit(MasterTemplateLibraryMutation.UpdateResizedFilter, {
                ...filter,
                type: FilterType.Resized
            });
        },

        updateFamilyFilter({ commit }, filter: { values: string[] }) {
            commit(MasterTemplateLibraryMutation.UpdateFamilyFilter, {
                ...filter,
                type: FilterType.Family
            });
        },

        updateSizeFilter({ commit }, filter: { values: string[] }) {
            commit(MasterTemplateLibraryMutation.UpdateSizeFilter, {
                ...filter,
                type: FilterType.Size
            });
        },

        updateTextFilter({ commit }, filter: { values: string[] }) {
            commit(MasterTemplateLibraryMutation.UpdateTextFilter, {
                ...filter,
                type: FilterType.Text
            });
        },

        updateTypeFilter({ commit }, filter: { values: string[] }) {
            commit(MasterTemplateLibraryMutation.UpdateTypeFilter, {
                ...filter,
                type: FilterType.Type
            });
        }
    }
};

export default masterTemplateLibraryModule;
